import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';

import { LabelBadge } from '../../shared/components/label-badge/label-badge';

/**
 * Card for one group of tenant settings. Projects `app-setting-row`s, shows a
 * transient "Saved" note after a successful save, and — while the projected
 * rows hold unsaved edits — a footer with Discard / Save changes. The parent
 * owns all state; this component is purely presentational.
 */
@Component({
  selector: 'app-setting-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, LabelBadge],
  host: { class: 'block' },
  template: `
    <section *transloco="let t" [class]="flat() ? 'block' : 'rounded-card bg-card p-6 shadow-hairline'">
      <div class="flex items-baseline justify-between gap-4">
        <div>
          <div class="flex items-center gap-2.5">
            <h2 class="font-display text-xl font-semibold leading-7 text-ink-1">{{ title() }}</h2>
            @if (badge()) {
              @if (badgeNeutral()) {
                <app-label-badge>{{ badge() }}</app-label-badge>
              } @else {
                <app-label-badge dot="cyan">{{ badge() }}</app-label-badge>
              }
            }
          </div>
          <p class="mt-0.5 text-xs text-ink-3">{{ subtitle() }}</p>
        </div>
        @if (saved()) {
          <span class="flex-none text-xs font-semibold text-success" role="status">
            {{ t('tenantSettings.saved') }}
          </span>
        }
      </div>

      <div class="mt-2 divide-y divide-hairline">
        <ng-content />
      </div>

      @if (dirty()) {
        <div class="mt-4 flex items-center gap-2 border-t border-hairline pt-4">
          <span class="mr-auto text-xs text-ink-3">{{ t('tenantSettings.unsavedChanges') }}</span>
          <button
            type="button"
            (click)="discard.emit()"
            class="cursor-pointer rounded-control px-4 py-2 text-[13px] font-semibold text-ink-2 hover:bg-sunken hover:text-ink-1"
          >
            {{ t('tenantSettings.discard') }}
          </button>
          <button
            type="button"
            (click)="save.emit()"
            [disabled]="!canSave() || saving()"
            class="cursor-pointer rounded-control bg-action px-4 py-2 text-[13px] font-semibold text-action-text hover:brightness-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {{ t('tenantSettings.saveChanges') }}
          </button>
        </div>
      }
    </section>
  `,
})
export class SettingSection {
  readonly title = input.required<string>();
  /** Suppress the card wrapper — use inside an already-wrapped container. */
  readonly flat = input(false);
  readonly subtitle = input('');
  /** Optional cyan-dot badge next to the title (e.g. "Users can override"). */
  readonly badge = input('');
  /** Renders the badge without the cyan dot (neutral/informational). */
  readonly badgeNeutral = input(false);
  readonly dirty = input(false);
  /** Transient post-save confirmation flag. */
  readonly saved = input(false);
  /** False disables Save (e.g. a numeric field is out of range). */
  readonly canSave = input(true);
  /** True while this section's save request is in flight. */
  readonly saving = input(false);

  readonly save = output<void>();
  readonly discard = output<void>();
}
